import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useLingui } from '@lingui/react';
import { Repeat, Pencil } from 'lucide-react';
import type { Shift } from '@/types/shifts';
import { ShiftChangedBadge, ShiftStatusBadge } from './ShiftStatusBadge';
import { isChangedAfterPublish } from './shiftLifecycle';
import { ShiftRequestsSection, isOpenShift, useShiftRequestMark } from './openShifts';
import { ShiftTypeBadge } from './ShiftTypeBadge';
import { formatDateLong, formatDateTime, formatDecimal } from '@/lib/format';
import { ShiftStatus } from '@/types/shifts';
import {
  isValidShiftStatus,
  isValidShiftType,
} from '@/utils/shiftHelpers';

interface ShiftDetailDialogProps {
  shift: Shift | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit?: (shift: Shift) => void;
  onDelete?: (shift: Shift) => void;
  onDeleteSeries?: (shift: Shift) => void;
  /**
   * The shift lies outside the caller's groups (#2043): no Edit and no Delete, and one line
   * that says why.
   */
  readOnly?: boolean;
}

export function ShiftDetailDialog({
  shift,
  open,
  onOpenChange,
  onEdit,
  onDelete,
  onDeleteSeries,
  readOnly = false,
}: ShiftDetailDialogProps) {
  const { i18n } = useLingui();
  const requestMark = useShiftRequestMark(shift?.id);

  if (!shift) return null;
  // With a requests section (#2040) its HelpTip would take the first focus and open over the
  // details; the dialog keeps the focus on itself instead.
  const hasRequestSection = isOpenShift(shift) || !!requestMark?.swap || (requestMark?.claims.length ?? 0) > 0;

  const employeeName = shift.employee
    ? `${shift.employee.firstname} ${shift.employee.lastname}`
    : isOpenShift(shift)
      ? i18n._({ id: 'shifts.open.nobody', message: 'Open shift, nobody assigned' })
      : i18n._({ id: 'shifts.unknownEmployee', message: 'Unknown' });

  const shiftDate = shift.shift_date
    ? formatDateLong(shift.shift_date, i18n.locale)
    : '-';

  const timeRange = `${shift.start_time} - ${shift.end_time}`;
  const changed = isChangedAfterPublish(shift);

  // Lifecycle lines (#2038), in the order they happen.
  const historyLines: { key: string; text: string }[] = [];
  if (shift.published_at) {
    historyLines.push({
      key: 'published',
      text: i18n._({
        id: 'shifts.lifecycle.publishedAt',
        message: 'Published on {date}',
        values: { date: formatDateTime(shift.published_at, i18n.locale) },
      }),
    });
  }
  if (shift.status === ShiftStatus.CONFIRMED && shift.confirmed_at) {
    const date = formatDateTime(shift.confirmed_at, i18n.locale);
    let text: string;
    if (shift.confirmed_by_employee === true) {
      text = i18n._({
        id: 'shifts.lifecycle.confirmedByEmployee',
        message: 'Confirmed on {date} by {name}',
        values: { date, name: employeeName },
      });
    } else if (shift.confirmed_by_employee === false) {
      text = i18n._({
        id: 'shifts.lifecycle.confirmedByAdmin',
        message: 'Confirmed on {date} by the administration',
        values: { date },
      });
    } else {
      text = i18n._({ id: 'shifts.lifecycle.confirmedAt', message: 'Confirmed on {date}', values: { date } });
    }
    historyLines.push({ key: 'confirmed', text });
  }
  if (changed && shift.changed_after_publish_at) {
    historyLines.push({
      key: 'changed',
      text: i18n._({
        id: 'shifts.lifecycle.changedNotified',
        message: 'Changed on {date}, the employee was notified',
        values: { date: formatDateTime(shift.changed_after_publish_at, i18n.locale) },
      }),
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-xl"
        onOpenAutoFocus={hasRequestSection ? (event) => event.preventDefault() : undefined}
      >
        <DialogHeader>
          <DialogTitle>
            {i18n._({ id: 'shifts.detail.title', message: 'Shift Details' })}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {i18n._({
              id: 'shifts.detail.description',
              message: 'Details of the selected shift',
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Employee */}
          <div className="flex justify-between items-start">
            <span className="text-sm text-muted-foreground">
              {i18n._({ id: 'shifts.detail.employee', message: 'Employee' })}
            </span>
            <span className="font-medium text-right">{employeeName}</span>
          </div>

          {/* Date */}
          <div className="flex justify-between items-start">
            <span className="text-sm text-muted-foreground">
              {i18n._({ id: 'shifts.detail.date', message: 'Date' })}
            </span>
            <span className="font-medium">{shiftDate}</span>
          </div>

          {/* Time */}
          <div className="flex justify-between items-start">
            <span className="text-sm text-muted-foreground">
              {i18n._({ id: 'shifts.detail.time', message: 'Time' })}
            </span>
            <span className="font-medium">{timeRange}</span>
          </div>

          {/* Break */}
          <div className="flex justify-between items-start">
            <span className="text-sm text-muted-foreground">
              {i18n._({ id: 'shifts.detail.break', message: 'Break' })}
            </span>
            <span className="font-medium">
              {shift.break_start && shift.break_end
                ? `${shift.break_start} - ${shift.break_end} (${shift.break_minutes} ${i18n._({ id: 'shifts.detail.minutes', message: 'min.' })})`
                : `${shift.break_minutes} ${i18n._({ id: 'shifts.detail.minutes', message: 'min.' })}`}
            </span>
          </div>

          {/* Net Hours */}
          <div className="flex justify-between items-start">
            <span className="text-sm text-muted-foreground">
              {i18n._({ id: 'shifts.detail.netHours', message: 'Net Hours' })}
            </span>
            <span className="font-medium">
              {shift.net_hours != null
                ? i18n._({
                    id: 'shifts.detail.hoursValue',
                    message: '{hours} h',
                    values: { hours: formatDecimal(shift.net_hours, i18n.locale, 2) },
                  })
                : '–'}
            </span>
          </div>

          {/* Type & Status Badges */}
          <div className="flex justify-between items-start">
            <span className="text-sm text-muted-foreground">
              {i18n._({
                id: 'shifts.detail.typeAndStatus',
                message: 'Type & Status',
              })}
            </span>
            <div className="flex flex-wrap justify-end gap-2">
              {isValidShiftType(shift.shift_type) && (
                <ShiftTypeBadge type={shift.shift_type} />
              )}
              {isValidShiftStatus(shift.status) && (
                <ShiftStatusBadge status={shift.status} />
              )}
              {changed && <ShiftChangedBadge />}
            </div>
          </div>

          {/* Lifecycle (#2038) */}
          {historyLines.length > 0 && (
            <div className="flex justify-between items-start gap-4">
              <span className="text-sm text-muted-foreground">
                {i18n._({ id: 'shifts.detail.history', message: 'History' })}
              </span>
              <ul className="text-sm text-right space-y-0.5" data-testid="shift-history">
                {historyLines.map((line) => (
                  <li key={line.key}>{line.text}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Series Indicator */}
          {shift.pattern_id && (
            <div className="flex justify-between items-start">
              <span className="text-sm text-muted-foreground">
                {i18n._({
                  id: 'shifts.detail.series',
                  message: 'Series',
                })}
              </span>
              <Badge
                variant="secondary"
                className="flex items-center gap-1"
                data-testid="series-badge"
              >
                <Repeat className="h-3 w-3" />
                {i18n._({
                  id: 'shifts.series.partOfSeries',
                  message: 'Part of a series',
                })}
              </Badge>
            </div>
          )}

          {/* Exception Indicator */}
          {shift.is_exception && (
            <div className="flex justify-between items-start">
              <span className="text-sm text-muted-foreground">
                {i18n._({
                  id: 'shifts.detail.modification',
                  message: 'Change',
                })}
              </span>
              <Badge
                variant="outline"
                className="flex items-center gap-1 border-warning-on-surface/40 bg-warning-muted text-warning-on-surface"
                data-testid="exception-badge"
              >
                <Pencil className="h-3 w-3" />
                {i18n._({
                  id: 'shifts.series.modified',
                  message: 'Individually modified',
                })}
              </Badge>
            </div>
          )}

          {/* Groups */}
          {shift.groups && shift.groups.length > 0 && (
            <div className="flex justify-between items-start">
              <span className="text-sm text-muted-foreground">
                {i18n._({ id: 'shifts.detail.groups', message: 'Groups' })}
              </span>
              <div className="flex flex-wrap gap-1 justify-end max-w-[200px]">
                {shift.groups.map((group) => (
                  <Badge key={group.id} variant="outline">
                    {group.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Claims of an open shift, or a pending swap (#2040) */}
          <ShiftRequestsSection shift={shift} onDecided={(approved) => approved && onOpenChange(false)} />

          {/* Notes */}
          {shift.notes && (
            <div className="flex flex-col gap-1">
              <span className="text-sm text-muted-foreground">
                {i18n._({ id: 'shifts.detail.notes', message: 'Notes' })}
              </span>
              <p className="text-sm bg-muted p-2 rounded">{shift.notes}</p>
            </div>
          )}
        </div>

        {readOnly && (
          <p className="text-sm text-muted-foreground" data-testid="shift-detail-read-only">
            {i18n._({
              id: 'shifts.detail.readOnlyOtherGroup',
              message: 'You can view this shift but not change it: it belongs to a group you do not lead.',
            })}
          </p>
        )}

        <DialogFooter className="flex-wrap gap-2 sm:space-x-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {i18n._({ id: 'common.close', message: 'Close' })}
          </Button>
          {!readOnly && (
            <>
              <Button
                variant="secondary"
                onClick={() => onEdit?.(shift)}
                disabled={!onEdit}
              >
                {i18n._({ id: 'common.edit', message: 'Edit' })}
              </Button>
              <Button
                variant="destructive"
                onClick={() => onDelete?.(shift)}
                disabled={!onDelete}
              >
                {i18n._({ id: 'common.delete', message: 'Delete' })}
              </Button>
              {/* Delete Series button - only shown for recurring shifts */}
              {shift.pattern_id && (
                <Button
                  variant="destructive"
                  onClick={() => onDeleteSeries?.(shift)}
                  disabled={!onDeleteSeries}
                  data-testid="delete-series-button"
                >
                  {i18n._({
                    id: 'shifts.series.deleteSeries',
                    message: 'Delete Series',
                  })}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
